import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ── Design tokens (same Acumen brand palette) ────────────────────────────────
const T = {
  navy: "#A81026",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
};

// ── Mode accent colours ───────────────────────────────────────────────────────
const M = {
  competing: "#A80D2C",
  collaborating: "#1A6B3C",
  compromising: "#7B3F9E",
  avoiding: "#D79A1E",
  accommodating: "#1B6FA0",
};

// ── Mode background tints (solid, like VAK style.bg) ─────────────────────────
const M_BG = {
  competing:     "#fde8ec",
  collaborating: "#e6f4ed",
  compromising:  "#f0ebfa",
  avoiding:      "#fef3c7",
  accommodating: "#e8f4fc",
};

// ── Static mode definitions (mirrored from TKResults.jsx) ────────────────────
const MODE_DEFINITIONS = [
  {
    key: "competing",
    label: "Competing",
    subtitle: "(Forcing)",
    tagline: '"Assertive & uncooperative — winning-oriented"',
    color: M.competing,
    max: 12,
    overview:
      "Is assertive and uncooperative — pursues own concerns at the other person's expense. A power-oriented mode in which one uses rank, argument, or economic leverage to win their position.",
    uses: [
      "When quick, decisive action is vital — e.g. emergencies.",
      "On important issues where unpopular courses of action need implementing — e.g. cost cutting, enforcing rules, discipline.",
      "On issues vital to company welfare when you know you're right.",
      "To protect yourself against people who take advantage of non-competitive behavior.",
    ],
    highWarning:
      "You may be surrounded by 'yes' people who have learned it's unwise to disagree with you, cutting you off from important information. Subordinates may act more confident than they feel, reducing their ability to learn and share uncertainties.",
    lowWarning:
      "You may often feel powerless in situations, struggle to take a firm stand even when you see the need, or hesitate to use legitimate authority — hindering your effectiveness.",
  },
  {
    key: "collaborating",
    label: "Collaborating",
    subtitle: "(Problem Solving)",
    tagline: '"Assertive & cooperative — win-win seeker"',
    color: M.collaborating,
    max: 12,
    overview:
      "Both assertive and cooperative — works with the other person to find a solution which fully satisfies both parties' concerns. Involves digging into an issue to identify underlying concerns and finding an alternative that meets both sets of needs.",
    uses: [
      "To find an integrative solution when both sets of concerns are too important to be compromised.",
      "When your objective is to learn — e.g. testing your own assumptions, understanding the views of others.",
      "To merge insights from people with different perspectives on a problem.",
      "To gain commitment by incorporating others' concerns into a consensual decision.",
      "To work through hard feelings which have been interfering with an interpersonal relationship.",
    ],
    highWarning:
      "You may spend time discussing issues in depth that do not deserve it. Collaboration takes time and energy — trivial problems don't require optimal solutions. Overuse sometimes represents a desire to minimize risk by diffusing responsibility or postponing action.",
    lowWarning:
      "You may miss collaborative opportunities and deprive yourself of mutual gains. Subordinates may feel uncommitted to your decisions if their concerns aren't incorporated.",
  },
  {
    key: "compromising",
    label: "Compromising",
    subtitle: "(Sharing)",
    tagline: '"Middle-ground seeker — split-the-difference"',
    color: M.compromising,
    max: 12,
    overview:
      "Intermediate in both assertiveness and cooperativeness. Seeks mutually acceptable solutions that partially satisfy both parties. Falls between competing and accommodating — faster than collaborating but gives up more than competing.",
    uses: [
      "When goals are moderately important, but not worth the effort or disruption of more assertive modes.",
      "When two opponents with equal power are strongly committed to mutually exclusive goals.",
      "To achieve temporary settlements on complex issues.",
      "To arrive at expedient solutions under time pressure.",
      "As a backup when collaboration or competition fails to be successful.",
    ],
    highWarning:
      "You may concentrate so heavily on the practicalities and tactics of compromise that you lose sight of larger principles, values, or long-term objectives. An emphasis on bargaining can create a cynical climate that undermines interpersonal trust.",
    lowWarning:
      "You may find it too difficult or embarrassing to be effective in bargaining situations, or find it hard to make concessions — making it harder to exit mutually destructive deadlocks gracefully.",
  },
  {
    key: "avoiding",
    label: "Avoiding",
    subtitle: "(Withdrawal)",
    tagline: '"Unassertive & uncooperative — diplomatic withdrawer"',
    color: M.avoiding,
    max: 12,
    overview:
      "Neither assertive nor cooperative — does not immediately pursue own or others' concerns. Diplomatically sidesteps, postpones, or withdraws from threatening situations without addressing the conflict.",
    uses: [
      "When an issue is trivial or of only passing importance, or when other more important issues are pressing.",
      "When you perceive no chance of satisfying your concerns — e.g. low power or very difficult-to-change situations.",
      "When the potential costs of confronting a conflict outweigh the benefits of resolution.",
      "To let people cool down and reduce tensions to a productive level.",
      "When gathering more information outweighs the advantages of an immediate decision.",
      "When others can resolve the conflict more effectively.",
    ],
    highWarning:
      "People may struggle to get your input on issues, decisions may be made by default, and others may be 'walking on eggshells' around unresolved tensions that need to be faced.",
    lowWarning:
      "You may inadvertently hurt others' feelings or stir up hostilities. You may feel harried or overwhelmed because you haven't set priorities around which issues are truly worth raising.",
  },
  {
    key: "accommodating",
    label: "Accommodating",
    subtitle: "(Smoothing)",
    tagline: '"Unassertive & cooperative — relationship preserver"',
    color: M.accommodating,
    max: 12,
    overview:
      "Unassertive and cooperative — the opposite of competing. Neglects own concerns to satisfy the concerns of the other person. There is an element of self-sacrifice in this mode: selfless generosity, yielding to another's view, or obeying when one would prefer not to.",
    uses: [
      "When you realize that you are wrong — to allow a better position to be heard and to show that you are reasonable.",
      "When the issue is much more important to the other person than to yourself.",
      "To build up social credits for later issues which are important to you.",
      "When continued competition would only damage your cause — when you are outmatched.",
      "When preserving harmony and avoiding disruption are especially important.",
      "To aid in the development of subordinates by allowing them to experiment and learn from mistakes.",
    ],
    highWarning:
      "Your own ideas and concerns may not get the attention they deserve, reducing your influence, respect, and recognition. Discipline may also become lax.",
    lowWarning:
      "You may have trouble building goodwill with others, be seen as unreasonable, struggle to admit when you are wrong, or find it hard to know when to give up.",
  },
];

// ── Normative percentile cutpoints ───────────────────────────────────────────
const TK_PERCENTILE_CUTPOINTS = {
  competing: { high: 9, low: 4 },
  collaborating: { high: 9, low: 6 },
  compromising: { high: 9, low: 5 },
  avoiding: { high: 9, low: 4 },
  accommodating: { high: 6, low: 3 },
};

// ── Pentagon radar SVG ────────────────────────────────────────────────────────
function PentagonSVG({ modesData }) {
  const size = 480;
  const cx = size / 2;
  const cy = size / 2;
  const r = 110;
  const levels = 4;
  const maxVal = 12;
  const n = modesData.length;

  const angleOf = (i) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pointAt = (angle, radius) => ({
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  });

  const gridPolygons = Array.from({ length: levels }, (_, lvl) => {
    const frac = (lvl + 1) / levels;
    return modesData
      .map((_, i) => {
        const p = pointAt(angleOf(i), r * frac);
        return `${p.x},${p.y}`;
      })
      .join(" ");
  });

  const dataPoints = modesData
    .map(({ score }, i) => {
      const val = score ?? 0;
      const p = pointAt(angleOf(i), r * (val / maxVal));
      return `${p.x},${p.y}`;
    })
    .join(" ");

  const axes = modesData.map((_, i) => {
    const end = pointAt(angleOf(i), r);
    return { x1: cx, y1: cy, x2: end.x, y2: end.y };
  });

  const labels = modesData.map(({ def, score }, i) => {
    const angle = angleOf(i);
    const lp = pointAt(angle, r + 38);
    const anchor = Math.abs(lp.x - cx) < 8 ? "middle" : lp.x < cx ? "end" : "start";
    return { x: lp.x, y: lp.y, label: def.label, score, anchor, color: def.color };
  });

  return (
    <svg
      viewBox={`0 40 ${size} ${size}`}
      width={size}
      height={size}
      style={{ display: "block", margin: "0 auto", overflow: "visible" }}
    >
      {gridPolygons.map((pts, i) => (
        <polygon
          key={i}
          points={pts}
          fill="none"
          stroke="#DCE5EF"
          strokeWidth={i === levels - 1 ? 1.5 : 0.8}
        />
      ))}
      {axes.map((a, i) => (
        <line key={i} x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="#DCE5EF" strokeWidth={1} />
      ))}
      <polygon
        points={dataPoints}
        fill={`${T.navy}25`}
        stroke={T.navy}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {modesData.map(({ score, def }, i) => {
        const val = score ?? 0;
        const p = pointAt(angleOf(i), r * (val / maxVal));
        return (
          <circle key={i} cx={p.x} cy={p.y} r={6} fill={def.color} stroke="#fff" strokeWidth={1.5} />
        );
      })}
      {labels.map(({ x, y, label, score, anchor, color }, i) => (
        <g key={i}>
          <text
            x={x} y={y - 5}
            textAnchor={anchor}
            fontSize="9"
            fontWeight="700"
            fill={T.slate}
            style={{ textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}
          >
            {label.toUpperCase()}
          </text>
          {score != null && (
            <text
              x={x} y={y + 13}
              textAnchor={anchor}
              fontSize="13"
              fontWeight="700"
              fill={color}
              style={{ fontFamily: "Arial, sans-serif" }}
            >
              {score}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

// ── Percentile chart SVG ─────────────────────────────────────────────────────
function TKPercentileChart({ scores }) {
  const modeKeys = ["competing", "collaborating", "compromising", "avoiding", "accommodating"];
  const modeLabels = ["Competing", "Collaborating", "Compromising", "Avoiding", "Accommodating"];

  const svgW = 800, svgH = 560;
  const colW = 96, colGap = 14;
  const chartLeft = 30, chartTop = 80, chartBottom = 410;
  const chartH = chartBottom - chartTop;

  const TK_PERCENTILE_CUTPOINTS = {
    competing: { lowMax: 3, midMax: 7 },
    collaborating: { lowMax: 5, midMax: 8 },
    compromising: { lowMax: 4, midMax: 8 },
    avoiding: { lowMax: 3, midMax: 6 },
    accommodating: { lowMax: 4, midMax: 7 },
  };

  function scoreToYCustom(score, cut) {
    const { lowMax, midMax } = cut;

    const lowHeight = chartH * 0.25;
    const midHeight = chartH * 0.5;
    const highHeight = chartH * 0.25;

    if (score <= lowMax) {
      const ratio = score / lowMax;
      return chartBottom - ratio * lowHeight;
    }

    if (score <= midMax) {
      const ratio = (score - lowMax) / (midMax - lowMax);
      return chartBottom - lowHeight - ratio * midHeight;
    }

    const ratio = (score - midMax) / (12 - midMax);
    return chartTop + (1 - ratio) * highHeight;
  }

  const cols = modeKeys.map((key, i) => ({
    key,
    label: modeLabels[i],
    color: M[key],
    x: chartLeft + i * (colW + colGap),
    score: scores?.[key] ?? null,
    cut: TK_PERCENTILE_CUTPOINTS[key],
  }));

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ maxWidth: 760, margin: "0 auto", display: "block" }}>

      {/* 🔥 Gradients */}
      <defs>
        {cols.map(({ key, color }) => (
          <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.65" />
            <stop offset="100%" stopColor={color} stopOpacity="0.15" />
          </linearGradient>
        ))}
      </defs>

      {/* Title */}
      <text x={(svgW / 2) - 100} y={22} textAnchor="middle" fontSize="13" fontWeight="700" fill={T.ink}>
        GRAPHING YOUR TKI SCORES
      </text>

      {/* ✅ LEFT SIDE LABELS (UNCHANGED) */}
      {[
        { label: "HIGH 25%", y: chartTop + chartH * 0.125 },
        { label: "MIDDLE 50%", y: chartTop + chartH * 0.5 },
        { label: "LOW 25%", y: chartBottom - chartH * 0.125 },
      ].map(({ label, y }) => (
        <text
          key={label}
          x={18}
          y={y}
          fontSize="8"
          fontWeight="700"
          fill={T.slate}
          textAnchor="middle"
          transform={`rotate(-90,18,${y})`}>
          {label}
        </text>
      ))}

      {/* Columns */}
      {cols.map(({ key, label, color, x, score, cut }) => {

        const scoreDotY = score !== null ? scoreToYCustom(score, cut) : null;
        const lowY = scoreToYCustom(cut.lowMax, cut);
        const midY = scoreToYCustom(cut.midMax, cut);

        return (
          <g key={key}>

            {/* ✅ NEW: Fill up to score */}
            {score !== null && (
              <rect
                x={x}
                y={scoreDotY}
                width={colW}
                height={chartBottom - scoreDotY}
                fill={`url(#grad-${key})`}
                rx="6"
              />
            )}

            {/* Column border */}
            <rect
              x={x}
              y={chartTop}
              width={colW}
              height={chartH}
              fill="none"
              stroke={T.line}
            />

            {/* ✅ DOTTED CUT LINES (KEPT) */}
            <line
              x1={x}
              y1={lowY}
              x2={x + colW}
              y2={lowY}
              stroke={color}
              strokeDasharray="4,3"
              opacity={0.7}
            />

            <line
              x1={x}
              y1={midY}
              x2={x + colW}
              y2={midY}
              stroke={color}
              strokeDasharray="4,3"
              opacity={0.7}
            />

            {/* Numbers */}
            {Array.from({ length: 13 }, (_, s) => (
              <text
                key={s}
                x={x + colW / 2}
                y={scoreToYCustom(s, cut) + 4}
                fontSize="9"
                fill="#374151"
                textAnchor="middle">
                {s}
              </text>
            ))}

            {/* Score dot */}
            {scoreDotY !== null && (
              <g>
                <circle cx={x + colW / 2} cy={scoreDotY} r={14} fill={color} />
                <text
                  x={x + colW / 2}
                  y={scoreDotY + 4}
                  fontSize="12"
                  fontWeight="700"
                  fill="#fff"
                  textAnchor="middle">
                  {score}
                </text>
              </g>
            )}

            {/* Label */}
            <text
              x={x + colW / 2}
              y={chartTop - 10}
              fontSize="9"
              fontWeight="700"
              fill={color}
              textAnchor="middle">
              {label}
            </text>
          </g>
        );
      })}

      {/* Footer */}
      <text x={(svgW / 2) - 100} y={svgH - 4} fontSize="10" fill={T.slate} textAnchor="middle">
        Scores visualised with percentile zones and dynamic performance fill.
      </text>
    </svg>
  );
}

// ── Assertiveness / Cooperativeness position grid ────────────────────────────
function TKPositionGrid({ scores }) {
  const modes = [
    { key: "competing", label: "COMPETING", x: 60, y: 50, color: M.competing },
    { key: "collaborating", label: "COLLABORATING", x: 470, y: 50, color: M.collaborating },
    { key: "compromising", label: "COMPROMISING", x: 265, y: 215, color: M.compromising },
    { key: "avoiding", label: "AVOIDING", x: 60, y: 390, color: M.avoiding },
    { key: "accommodating", label: "ACCOMMODATING", x: 470, y: 390, color: M.accommodating },
  ];

  const bw = 190, bh = 148;
  const svgW = 820, svgH = 640;   // expanded to prevent edge clipping

  const maxScore = scores ? Math.max(...Object.values(scores)) : 0;

  const axisX = 22;               // shifted right to give room for rotated Y-label
  const axisTop = 30, axisBot = 565;
  const axisH = axisBot - axisTop;

  const axisY = 578;
  const axisLeft = 30, axisRight = 720;
  const axisW = axisRight - axisLeft;

  const maxAxisScore = 24;

  const assertScore = scores ? (scores.competing ?? 0) + (scores.collaborating ?? 0) : null;
  const coopScore = scores ? (scores.collaborating ?? 0) + (scores.accommodating ?? 0) : null;

  const assertDotY = assertScore !== null ? axisBot - (assertScore / maxAxisScore) * axisH : null;
  const coopDotX = coopScore !== null ? axisLeft + (coopScore / maxAxisScore) * axisW : null;

  const pointX = coopDotX;
  const pointY = assertDotY;

  let activeMode = null;
  if (pointX !== null && pointY !== null) {
    const midX = (axisLeft + axisRight) / 2;
    const midY = (axisTop + axisBot) / 2;
    const dx = Math.abs(pointX - midX);
    const dy = Math.abs(pointY - midY);
    if (dx < 80 && dy < 80) activeMode = "compromising";
    else if (pointY < midY && pointX < midX) activeMode = "competing";
    else if (pointY < midY && pointX > midX) activeMode = "collaborating";
    else if (pointY > midY && pointX < midX) activeMode = "avoiding";
    else activeMode = "accommodating";
  }

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ maxWidth: 760, margin: "0 auto", display: "block", overflow: "visible" }}>

      {/* Grid lines */}
      {[0, 6, 12, 18, 24].map(val => (
        <line key={`h-${val}`} x1={axisLeft} y1={axisBot - (val / maxAxisScore) * axisH} x2={axisRight} y2={axisBot - (val / maxAxisScore) * axisH} stroke="#eee" />
      ))}
      {[0, 6, 12, 18, 24].map(val => (
        <line key={`v-${val}`} x1={axisLeft + (val / maxAxisScore) * axisW} y1={axisTop} x2={axisLeft + (val / maxAxisScore) * axisW} y2={axisBot} stroke="#eee" />
      ))}

      {/* Axis lines */}
      <line x1={axisX} y1={axisTop} x2={axisX} y2={axisBot} stroke="#555" strokeWidth={1.5} />
      <line x1={axisLeft} y1={axisY} x2={axisRight} y2={axisY} stroke="#555" strokeWidth={1.5} />

      {/* Axis labels — Y */}
      <text x={10} y={(axisTop + axisBot) / 2} textAnchor="middle" fontSize="11" fontWeight="700"
        fill={M.competing} transform={`rotate(-90,10,${(axisTop + axisBot) / 2})`}
        style={{ fontFamily: "Arial, sans-serif", letterSpacing: "2px" }}>ASSERTIVENESS</text>
      <text x={axisX - 5} y={axisTop + 8} fontSize="8" textAnchor="end" fill="#555" style={{ fontFamily: "Arial, sans-serif" }}>Assertive</text>
      <text x={axisX - 5} y={axisBot - 4} fontSize="8" textAnchor="end" fill="#555" style={{ fontFamily: "Arial, sans-serif" }}>Unassertive</text>

      {/* Axis labels — X */}
      <text x={(axisLeft + axisRight) / 2} y={svgH - 8} textAnchor="middle" fontSize="11" fontWeight="700"
        fill={M.collaborating} style={{ fontFamily: "Arial, sans-serif", letterSpacing: "2px" }}>COOPERATIVENESS</text>
      <text x={axisLeft + 30} y={axisY + 16} fontSize="8" textAnchor="middle" fill="#555" style={{ fontFamily: "Arial, sans-serif" }}>Uncooperative</text>
      <text x={axisRight - 30} y={axisY + 16} fontSize="8" textAnchor="middle" fill="#555" style={{ fontFamily: "Arial, sans-serif" }}>Cooperative</text>

      {/* Y-axis tick labels */}
      {[0, 6, 12, 18, 24].map(val => {
        const y = axisBot - (val / maxAxisScore) * axisH;
        return (
          <g key={`yl-${val}`}>
            <line x1={axisX - 4} y1={y} x2={axisX + 4} y2={y} stroke="#666" />
            <text x={axisX - 8} y={y + 3} fontSize="8" textAnchor="end" fill="#555" style={{ fontFamily: "Arial, sans-serif" }}>{val}</text>
          </g>
        );
      })}

      {/* X-axis tick labels */}
      {[0, 6, 12, 18, 24].map(val => {
        const x = axisLeft + (val / maxAxisScore) * axisW;
        return (
          <g key={`xl-${val}`}>
            <line x1={x} y1={axisY - 4} x2={x} y2={axisY + 4} stroke="#666" />
            <text x={x} y={axisY + 14} fontSize="8" textAnchor="middle" fill="#555" style={{ fontFamily: "Arial, sans-serif" }}>{val}</text>
          </g>
        );
      })}

      {/* Axis position dots */}
      {assertDotY !== null && <circle cx={axisX} cy={assertDotY} r={7} fill={T.navy} />}
      {coopDotX !== null && <circle cx={coopDotX} cy={axisY} r={7} fill={M.collaborating} />}

      {/* Score point + guide lines */}
      {pointX !== null && pointY !== null && (
        <g>
          <line x1={pointX} y1={axisTop} x2={pointX} y2={axisBot} stroke="#bbb" strokeDasharray="4 4" />
          <line x1={axisLeft} y1={pointY} x2={axisRight} y2={pointY} stroke="#bbb" strokeDasharray="4 4" />
          <circle cx={pointX} cy={pointY} r={10} fill={T.ink} />
        </g>
      )}

      {/* Mode boxes */}
      {modes.map(({ key, label, x, y, color }) => {
        const score = scores?.[key] ?? null;
        const isDominant = scores && score === maxScore && maxScore > 0;
        const isActive = key === activeMode;
        return (
          <g key={key}>
            <rect x={x} y={y} width={bw} height={bh} rx={8}
              fill={isActive ? `${color}22` : "#fff"}
              stroke={isActive ? color : (isDominant ? color : "#8B9B7A")}
              strokeWidth={isActive ? 3.5 : (isDominant ? 2.5 : 1.5)}
            />
            <text x={x + bw / 2} y={y + 30} textAnchor="middle" fontSize="10" fontWeight="700" fill={color}
              style={{ fontFamily: "Arial, sans-serif", letterSpacing: "1px" }}>{label}</text>
            {score !== null && (
              <>
                <text x={x + bw / 2} y={y + 78} textAnchor="middle" fontSize="30" fontWeight="800" fill={color}
                  style={{ fontFamily: "Arial, sans-serif" }}>{score}</text>
                <text x={x + bw / 2} y={y + 98} textAnchor="middle" fontSize="9" fill="#999"
                  style={{ fontFamily: "Arial, sans-serif" }}>out of 12</text>
              </>
            )}
            {isDominant && (
              <text x={x + bw / 2} y={y + 126} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={color}
                style={{ fontFamily: "Arial, sans-serif", letterSpacing: ".5px" }}>★ DOMINANT</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ── Date helper ───────────────────────────────────────────────────────────────
function formatDate(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function HiddenTKReport({ participantData, onComplete }) {
  const containerRef = useRef(null);

  // ── Load html2canvas + jsPDF once ─────────────────────────────────────────
  useEffect(() => {
    let pending = 2;
    const done = () => { pending--; };
    const load = (src, id) => {
      if (document.getElementById(id)) { done(); return; }
      const s = document.createElement("script");
      s.id = id;
      s.src = src;
      s.onload = done;
      s.onerror = done;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-tk-v1");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", "jspdf-tk-v1");
  }, []);

  // ── Generate PDF whenever participantData is set ──────────────────────────
  const hasGenerated = useRef(false);

  useEffect(() => {
    if (!participantData || hasGenerated.current) return;

    hasGenerated.current = true;

    const generate = async () => {
      try {
        await new Promise((res) => {
          const check = () => {
            if (window.html2canvas && window.jspdf?.jsPDF) {
              res();
              return;
            }
            setTimeout(check, 100);
          };
          check();
        });

        await new Promise((r) => setTimeout(r, 1400));

        const el = containerRef.current;
        if (!el) {
          onComplete?.();
          return;
        }

        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".tk-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth,
            height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX = 25.4 / 96;
          const naturalW = logicalW * MM_PER_PX;
          const naturalH = logicalH * MM_PER_PX;
          const scaleToW = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.userInfo?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`TK_Report_${safeName}.pdf`);
      } catch (err) {
        console.error(err);
      } finally {
        hasGenerated.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!participantData) return null;

  // ── Derive display values ─────────────────────────────────────────────────
  const {
    userInfo = {},
    scores = {},
    insightText = "",
    dominantModes = [],
    submission_time,
  } = participantData;

  const participant = userInfo.name || "—";
  const assessmentDate = submission_time ? formatDate(new Date(submission_time)) : "—";

  const modesForDisplay = MODE_DEFINITIONS.map((def) => ({
    def,
    score: scores[def.key] ?? null,
  }));

  // Sorted highest-score-first — used only on "The Five Conflict-Handling Modes" pages
  const modesForDisplaySorted = [...modesForDisplay].sort(
    (a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity)
  );

  // Dominant mode labels
  const dominantLabels = dominantModes
    .map((k) => MODE_DEFINITIONS.find((m) => m.key === k)?.label ?? k)
    .join(" & ");

  const dominantDef = MODE_DEFINITIONS.find((m) => m.key === dominantModes[0]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      {/* Font */}
      <link
        href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap"
        rel="stylesheet"
      />

      {/* ── All CSS scoped under .tk-pdf-root ── */}
      <style>{`
        .tk-pdf-root {
          font-family: 'Satoshi', sans-serif;
          background: #F5F8FC;
          color: #1D2733;
          line-height: 1.55;
        }
        .tk-pdf-root .report { max-width:1100px; margin:0 auto; padding:1.5rem 1rem 3rem; }

        /* ── Page shell ── */
        .tk-pdf-root .tk-page {
          background:#ffffff;
          border:1px solid #DCE5EF;
          border-radius:24px;
          box-shadow:0 14px 36px rgba(11,31,58,.10);
          overflow:hidden;
          margin-bottom:1.5rem;
        }

        /* ── Cover ── */
        .tk-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .tk-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
        .tk-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem; position:relative; z-index:2; }
        .tk-pdf-root .brand-left { display:flex; align-items:center; gap:1rem; }
        .tk-pdf-root .logo { width:60px; height:60px; border-radius:16px; display:grid; place-items:center; font-weight:900; font-size:1.1rem; letter-spacing:.06em; flex-shrink:0; }
        .tk-pdf-root .brand h1 { margin:0; font-size:1.2rem; letter-spacing:.07em; text-transform:uppercase; }
        .tk-pdf-root .brand p  { margin:.15rem 0 0; color:rgba(255,255,255,.72); font-size:0.85rem; }
        .tk-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }

        .tk-pdf-root .logo-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: 500px;
          height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        .tk-pdf-root .logo-panel-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          padding-left: 60px;
          padding-bottom: 25px;
        }
        .tk-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        .tk-pdf-root .cover-title { width:100%; }
        .tk-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:2.9rem;line-height:1.08; color:#FFFFFF;white-space:normal; word-wrap:break-word; max-width:100%; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .tk-pdf-root .cover-title p  { font-size:1.2rem; color:rgba(255,255,255,.82); width:100%; box-sizing:border-box; max-width:100%; text-align:justify; }
        .tk-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
        .tk-pdf-root .meta { background:rgba(255,255,255,.1);font-size: 1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
        .tk-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .tk-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .tk-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem;}
        .tk-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .tk-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:2rem; }

        /* ── Inner pages ── */
        .tk-pdf-root .page-inner { padding:2.5rem; }
        .tk-pdf-root .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:1.75rem; }
        .tk-pdf-root .section-kicker { font-size:0.72rem; text-transform:uppercase; letter-spacing:.12em; font-weight:700; color:#c62828; }
        .tk-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }

        /* ── Cards ── */
        .tk-pdf-root .card { background:#ffffff; border:1px solid #DCE5EF; border-radius:18px; padding:1.25rem; box-shadow:0 4px 14px rgba(16,24,40,.05); }
        .tk-pdf-root .card h3 { margin:0 0 .65rem; font-size:1.05rem; color:#1D2733; }
        .tk-pdf-root .lead { color:#334155; font-size:0.93rem; margin:0; }

        /* ── Inner sub-panel ── */
        .tk-pdf-root .sub-panel { background:#fbfdff; border:1px solid #DCE5EF; border-radius:14px; padding:1rem; }
        .tk-pdf-root .sub-panel h4 { margin:0 0 .6rem; font-size:.95rem; color:#1D2733; }

        /* ── Bars ── */
        .tk-pdf-root .bar-row { display:grid; grid-template-columns:175px 1fr 52px; gap:10px; align-items:center; margin-bottom:.7rem; }
        .tk-pdf-root .bar-label { font-size:0.85rem; font-weight:600; color:#1D2733; }
        .tk-pdf-root .track { height:11px; background:#EAF0F6; border-radius:999px; overflow:hidden; }
        .tk-pdf-root .fill  { height:100%; border-radius:999px; }
        .tk-pdf-root .score-val { text-align:right; font-size:0.85rem; font-weight:700; }

        /* ── Callout ── */
        .tk-pdf-root .callout { background:linear-gradient(180deg,#fff9e8,#fffdf2); border:1px solid #F2D58A; border-radius:14px; padding:.85rem 1rem; font-size:0.88rem; line-height:1.6; }

        /* ── Mode item (Page 3) ── */
        .tk-pdf-root .mode-item { border:1px solid #DCE5EF; border-left-width:4px; border-radius:14px; padding:.95rem; margin-bottom:.75rem; background:#fff; }
        .tk-pdf-root .mode-item.dominant { box-shadow:0 0 0 2px rgba(0,0,0,.07); }
        .tk-pdf-root .mode-item-header { display:flex; align-items:center; gap:.85rem; margin-bottom:.5rem; }
        .tk-pdf-root .mode-score-badge { width:52px; height:52px; border-radius:12px; display:grid; place-items:center; font-size:1.35rem; font-weight:800; color:white; flex-shrink:0; }
        .tk-pdf-root .mode-item-meta strong { display:block; font-size:.97rem; color:#1D2733; }
        .tk-pdf-root .mode-item-meta em     { font-style:italic; font-size:.82rem; color:#5B677A; }
        .tk-pdf-root .mode-dominant-tag { font-size:.72rem; font-weight:700; margin-left:.5rem; padding:2px 7px; border-radius:999px; }
        .tk-pdf-root .mode-item p { margin:0 0 .3rem; font-size:0.83rem; color:#334155; }
        .tk-pdf-root .mode-uses { padding-left:1rem; margin:.3rem 0; }
        .tk-pdf-root .mode-uses li { font-size:0.81rem; color:#334155; margin-bottom:.2rem; line-height:1.45; }

        /* ── Two-col ── */
        .tk-pdf-root .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1.2rem; }

        /* ── Warning boxes ── */
        .tk-pdf-root .warn-grid { display:grid; grid-template-columns:1fr 1fr; gap:.6rem; margin-top:.5rem; }
        .tk-pdf-root .warn-high { background:#fff9e8; border:1px solid #F2D58A; border-radius:10px; padding:.6rem .75rem; }
        .tk-pdf-root .warn-low  { background:#f0f7ff; border:1px solid #C0D8EF; border-radius:10px; padding:.6rem .75rem; }
        .tk-pdf-root .warn-label { font-size:.77rem; font-weight:700; margin-bottom:.25rem; }
        .tk-pdf-root .warn-text  { font-size:.79rem; color:#334155; line-height:1.45; margin:0; }

        /* ── Growth cards (Page 4) ── */
        .tk-pdf-root .growth-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:.85rem; margin-bottom:1.1rem; }
        .tk-pdf-root .growth-card { border-radius:12px; padding:.85rem 1rem; border:1px solid #DCE5EF; background:#fbfdff; }
        .tk-pdf-root .growth-card-title { font-size:.88rem; font-weight:700; margin-bottom:.35rem; }
        .tk-pdf-root .growth-card p { font-size:.81rem; color:#334155; margin:0; line-height:1.5; }

         /* ── Back cover ── */
        .tk-pdf-root .back-cover { min-height:1556px; color:white; }
        .tk-pdf-root .back-cover .page-inner { min-height:1556px; display:flex; flex-direction:column; justify-content:space-between; }
        .tk-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .tk-pdf-root .closing p  { max-width:100%; color:rgba(255,255,255,.82); font-size: 1.2rem; text-align:justify; }
        .tk-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
        .tk-pdf-root .contact { background:rgba(193,24,24,.1); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
        .tk-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .tk-pdf-root .contact strong { font-size:0.92rem; }
        .tk-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .tk-pdf-root .light { color:rgba(255,255,255,.66); }
      `}</style>

      <div className="tk-pdf-root">
        <main className="report">

          {/* ══════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════ */}
          <section className="tk-page cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
              minHeight: "1556px",
              position: "relative",
            }}>
              {/* Logo panel — SVG white wedge + logo image */}
              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>

              <div className="cover-inner">
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: 'relative', zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>Thomas-Kilmann Conflict Mode Assessment</h2>
                    <p>
                      A comprehensive individual profile report covering five Thomas-Kilmann
                      conflict-handling modes and their situational application, in a
                      consulting-ready visual format.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta">
                      <span>Participant</span>
                      <strong>{participant}</strong>
                    </div>
                    <div className="meta">
                      <span>Assessment Date</span>
                      <strong>{assessmentDate}</strong>
                    </div>
                    <div className="meta">
                      <span>Report Type</span>
                      <strong>Individual Conflict Mode Profile</strong>
                    </div>
                  </div>

                  {/* <div className="cover-grid" style={{ marginTop: "1rem" }}>
                    <div className="meta">
                      <span>Instrument</span>
                      <strong>Thomas-Kilmann (TKI)</strong>
                    </div>
                    <div className="meta">
                      <span>Dominant Mode</span>
                      <strong>{dominantLabels || "—"}</strong>
                    </div>
                    <div className="meta">
                      <span>Score Range</span>
                      <strong>0 – 12 per mode</strong>
                    </div>
                  </div> */}

                  <div className="cover-badges">
                    {["Executive Dashboard", "Five Conflict Modes", "Normative Comparison", "Growth Strategy"].map((b) => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note" style={{ position: 'relative', zIndex: 2 }}>
                  <div>Based on the Thomas-Kilmann Conflict Mode Instrument (TKI) framework.</div>
                  <div>Each mode scored out of 12.</div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 2 — EXECUTIVE DASHBOARD
          ══════════════════════════════════════════ */}
          <section className="tk-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Executive Dashboard</h2>
                </div>
                <div className="small">Mode scores out of 12</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".9rem", margin: "0 0 1.25rem" }}>
                This section presents your scores across all five conflict-handling modes.
                Scores reflect how frequently you tend to use each mode when facing conflict
                situations — higher scores indicate greater reliance on that approach.
              </p>

              {/* Two columns: pentagon left, callout + scores right */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", marginBottom: "1.25rem" }}>

                {/* Left — Pentagon radar */}
                <div className="sub-panel">
                  <h4>Your Conflict Mode Profile</h4>
                  <PentagonSVG modesData={modesForDisplay} />
                  {/* Legend */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".4rem", marginTop: ".65rem" }}>
                    {MODE_DEFINITIONS.map((def) => (
                      <div key={def.key} style={{ display: "flex", alignItems: "center", gap: ".4rem" }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: def.color, flexShrink: 0 }} />
                        <span style={{ fontSize: ".78rem", color: T.slate }}>{def.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right — Scores + callout */}
                <div className="sub-panel">
                  <h4>Mode Scores &amp; Evaluation Insight</h4>
                  <p style={{ color: T.slate, fontSize: ".82rem", margin: "0 0 .75rem",}}>
                    Scores reflect self-reported frequency of use across 30 paired-choice questionnaire items (0–12 per mode).
                  </p>

                  {/* Score tiles */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: ".55rem", marginBottom: ".85rem", flex: 1 }}>
                    {modesForDisplay.map(({ def, score }) => (
                      <div key={def.key} style={{
                        borderRadius: 12,
                        border: `1px solid ${def.color}30`,
                        padding: ".6rem .75rem",
                        background: `${def.color}08`,
                      }}>
                        <div style={{ fontSize: ".72rem", color: def.color, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em" }}>
                          {def.label}
                        </div>
                        <div style={{ fontSize: "1.7rem", fontWeight: 800, color: def.color, lineHeight: 1.1 }}>
                          {score ?? "—"}<span style={{ fontSize: ".75rem", color: T.slate, fontWeight: 400 }}>/12</span>
                        </div>
                        <div style={{ fontSize: ".7rem", color: T.slate, fontStyle: "italic" }}>{def.subtitle}</div>
                      </div>
                    ))}
                  </div>

                  {/* Dominant mode highlight */}
                  {dominantLabels && (
                    <div style={{
                      background: `linear-gradient(135deg,${T.navy}10,${T.blue}08)`,
                      border: `1px solid ${T.navy}30`,
                      borderRadius: 12,
                      padding: ".7rem .85rem",
                      marginBottom: ".75rem",
                    }}>
                      <div style={{ fontSize: ".72rem", fontWeight: 700, color: T.slate, textTransform: "uppercase", letterSpacing: ".07em" }}>
                        Dominant Mode
                      </div>
                      <div style={{ fontSize: "1.1rem", fontWeight: 700, color: T.navy, marginTop: ".15rem" }}>
                        {dominantLabels}
                      </div>
                      {dominantDef && (
                        <div style={{ fontSize: ".78rem", color: T.slate, fontStyle: "italic", marginTop: ".1rem" }}>
                          {dominantDef.tagline}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="callout">
                    <strong>Evaluation insight: </strong>
                    {insightText || "Your conflict mode profile reflects the mix of approaches you draw on when facing interpersonal and organizational conflict. Flexibility across all five modes is the hallmark of effective conflict management."}
                  </div>
                </div>
              </div>

              {/* Score bars at a glance */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
                <div className="card">
                  <h3>Mode Scores at a Glance <span style={{ fontSize: ".78rem", color: T.slate, fontWeight: 400 }}>— out of 12</span></h3>
                  {modesForDisplay.map(({ def, score }) => (
                    <div className="bar-row" key={def.key}>
                      <div className="bar-label">{def.label}</div>
                      <div className="track">
                        <div className="fill" style={{
                          width: `${((score ?? 0) / 12) * 100}%`,
                          background: def.color,
                        }} />
                      </div>
                      <div className="score-val" style={{ color: def.color }}>{score ?? "—"}/12</div>
                    </div>
                  ))}
                </div>

                <div className="card">
                  <h3>The Two Dimensions of Conflict</h3>
                  <p style={{ fontSize: ".84rem", color: T.slate, marginBottom: ".75rem" }}>
                    All five modes sit on two underlying axes that define the TKI model.
                  </p>
                  {[
                    { label: "Assertiveness →", color: T.navy, desc: "The degree to which you attempt to satisfy your own concerns. High: Competing, Collaborating. Low: Avoiding, Accommodating." },
                    { label: "Cooperativeness ↑", color: T.gold, desc: "The degree to which you attempt to satisfy the other person's concerns. High: Collaborating, Accommodating. Low: Competing, Avoiding." },
                  ].map(({ label, color, desc }) => (
                    <div key={label} style={{
                      background: "#fbfdff",
                      border: `1px solid ${T.line}`,
                      borderRadius: 10,
                      padding: ".65rem .8rem",
                      marginBottom: ".6rem",
                    }}>
                      <div style={{ fontWeight: 700, color, marginBottom: ".3rem", fontSize: ".88rem" }}>{label}</div>
                      <p style={{ margin: 0, fontSize: ".82rem", color: T.ink, lineHeight: 1.5 }}>{desc}</p>
                    </div>
                  ))}

                  <div style={{ marginTop: ".75rem", padding: ".65rem .8rem", background: "#f8f4eb", borderRadius: "12px", border: `1px solid ${T.gold}44` }}>
                    <div style={{ fontSize: ".72rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".25rem" }}>
                      Normative Bands
                    </div>
                    {[
                      { band: "High Use", cutoff: "Score ≥ 9", color: T.navy },
                      { band: "Middle Range", cutoff: "Score 3 – 8", color: T.gold },
                      { band: "Low Use", cutoff: "Score ≤ 3", color: T.slate },
                    ].map(({ band, cutoff, color }) => (
                      <div key={band} style={{ display: "flex", justifyContent: "space-between", fontSize: ".82rem", marginBottom: ".2rem" }}>
                        <span style={{ fontWeight: 700, color }}>{band}</span>
                        <span style={{ color: T.slate }}>{cutoff}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>


              {/* Assertiveness–Cooperativeness position grid */}

            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 3 — NORMATIVE COMPARISON & GROWTH
          ══════════════════════════════════════════ */}
          <section className="tk-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Normative Comparison &amp; Growth Plan</h2>
                </div>
                <div className="small">TKI norm group · Managers &amp; executives</div>
              </div>

              {/* Percentile chart */}
              <div className="card" style={{ marginBottom: "1.25rem" }}>
                <h3>Graphing Your TKI Scores</h3>
                <p className="lead" style={{ marginBottom: ".85rem" }}>
                  The chart below plots your score in each mode against the original TKI norm
                  group — managers at middle and upper levels of business and government.
                  The two <strong>bold horizontal lines</strong> mark the boundaries between
                  the <strong>High 25%</strong> (above 9), <strong>Middle 50%</strong> (3–9),
                  and <strong>Low 25%</strong> (below 3) bands. Your score appears as a filled
                  circle.
                </p>
                <TKPercentileChart scores={scores} />
              </div>
              
              <div className="callout">
                  <strong>Evaluation insight: </strong>
                  {insightText || "Flexibility across modes — informed by strong self-awareness — is the hallmark of sustained conflict management effectiveness."}
                </div>

              {/* Two-col: understanding scores + coaching note */}
              
            </div>
          </section>

          

          {/* ══════════════════════════════════════════
              PAGE 4 — FIVE CONFLICT MODES (detail)
          ══════════════════════════════════════════ */}
          <section className="tk-page">
            <div style={{ padding: "2.5rem 2.5rem 0" }}>
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">The Five Conflict-Handling Modes</h2>
                </div>
                <div className="small">Scored out of 12 per mode</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".88rem", margin: "0 0 1.1rem" }}>
                Each of us is capable of using all five conflict-handling modes; none of us
                can be characterised as having a single, rigid style. Your profile indicates
                the repertoire of skills you tend to use in the conflict situations you face.
              </p>
            </div>

            {/* All 5 modes — full width, stacked */}
            <div style={{ padding: "0 2.5rem" }}>
              {modesForDisplaySorted.slice(0, 3).map(({ def, score }) => {
                const isActiveDominant = dominantModes.includes(def.key);
                const modeBg = M_BG[def.key] || "#f5f5f5";
                return (
                <div className={`mode-item${isActiveDominant ? " dominant" : ""}`} key={def.key}
                  style={{
                    borderLeft:   `5px solid ${def.color}`,
                    borderTop:    isActiveDominant ? `1.5px solid ${def.color}` : `1px solid #DCE5EF`,
                    borderRight:  isActiveDominant ? `1.5px solid ${def.color}` : `1px solid #DCE5EF`,
                    borderBottom: isActiveDominant ? `1.5px solid ${def.color}` : `1px solid #DCE5EF`,
                    background:   isActiveDominant ? modeBg : "#fff",
                    boxShadow:    isActiveDominant ? `0 0 0 3px ${modeBg}` : "none",
                    marginBottom: ".85rem",
                  }}>
                  <div className="mode-item-header">
                    <div className="mode-score-badge" style={{ background: def.color }}>
                      {score ?? "—"}
                    </div>
                    <div className="mode-item-meta">
                      <strong style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: ".4rem" }}>
                        <span style={{ color: isActiveDominant ? def.color : "#1D2733" }}>
                          {def.label}{" "}
                          <span style={{ fontSize: ".82rem", color: T.slate, fontWeight: 400 }}>{def.subtitle}</span>
                        </span>
                        {isActiveDominant && (
                          <span style={{
                            fontSize: ".65rem", fontWeight: 800,
                            background: def.color, color: "#fff",
                            padding: "2px 8px", borderRadius: 999,
                            textTransform: "uppercase", letterSpacing: ".06em",
                            whiteSpace: "nowrap", flexShrink: 0,
                          }}>★ Your Dominant Mode</span>
                        )}
                      </strong>
                      <em>{def.tagline}</em>
                    </div>
                  </div>
                  <p>{def.overview}</p>
                  <div style={{ fontSize: ".8rem", fontWeight: 700, color: def.color, marginBottom: ".2rem" }}>When to use this mode:</div>
                  <ul className="mode-uses">
                    {def.uses.map((u, i) => <li key={i}>{u}</li>)}
                  </ul>
                  <div className="warn-grid">
                    <div className="warn-high">
                      <div className="warn-label" style={{ color: T.gold }}>⚠ If you scored High</div>
                      <p className="warn-text">{def.highWarning}</p>
                    </div>
                    <div className="warn-low">
                      <div className="warn-label" style={{ color: "#1B6FA0" }}>⚠ If you scored Low</div>
                      <p className="warn-text">{def.lowWarning}</p>
                    </div>
                  </div>
                </div>
                );
              })}

              {/* Context box */}
              {/* <div style={{ background: "#f8f4eb", border: `1px solid ${T.gold}55`, borderRadius: "14px", padding: "1rem", marginBottom: "0" }}>
                <div style={{ fontSize: ".72rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".5rem" }}>
                  Context &amp; Meaning
                </div>
                <p style={{ margin: 0, fontSize: ".83rem", color: T.ink, lineHeight: 1.6 }}>
                  There are no universal right answers. All five modes are useful — each
                  represents a set of valuable social skills. The effectiveness of any mode
                  depends on the specific conflict situation and the skill with which it
                  is deployed.
                </p>
              </div> */}
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 5 — FIVE CONFLICT MODES (detail)
          ══════════════════════════════════════════ */}
          <section className="tk-page">
            <div style={{ padding: "2.5rem 2.5rem 0" }}>
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">The Five Conflict-Handling Modes</h2>
                </div>
                <div className="small">Scored out of 12 per mode</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".88rem", margin: "0 0 1.1rem" }}>
                Each of us is capable of using all five conflict-handling modes; none of us
                can be characterised as having a single, rigid style. Your profile indicates
                the repertoire of skills you tend to use in the conflict situations you face.
              </p>
            </div>

            {/* All 5 modes — full width, stacked */}
            <div style={{ padding: "0 2.5rem" }}>
              {modesForDisplaySorted.slice(3).map(({ def, score }) => {
                const isActiveDominant = dominantModes.includes(def.key);
                const modeBg = M_BG[def.key] || "#f5f5f5";
                return (
                <div className={`mode-item${isActiveDominant ? " dominant" : ""}`} key={def.key}
                  style={{
                    borderLeft:   `5px solid ${def.color}`,
                    borderTop:    isActiveDominant ? `1.5px solid ${def.color}` : `1px solid #DCE5EF`,
                    borderRight:  isActiveDominant ? `1.5px solid ${def.color}` : `1px solid #DCE5EF`,
                    borderBottom: isActiveDominant ? `1.5px solid ${def.color}` : `1px solid #DCE5EF`,
                    background:   isActiveDominant ? modeBg : "#fff",
                    boxShadow:    isActiveDominant ? `0 0 0 3px ${modeBg}` : "none",
                    marginBottom: ".85rem",
                  }}>
                  <div className="mode-item-header">
                    <div className="mode-score-badge" style={{ background: def.color }}>
                      {score ?? "—"}
                    </div>
                    <div className="mode-item-meta">
                      <strong style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: ".4rem" }}>
                        <span style={{ color: isActiveDominant ? def.color : "#1D2733" }}>
                          {def.label}{" "}
                          <span style={{ fontSize: ".82rem", color: T.slate, fontWeight: 400 }}>{def.subtitle}</span>
                        </span>
                        {isActiveDominant && (
                          <span style={{
                            fontSize: ".65rem", fontWeight: 800,
                            background: def.color, color: "#fff",
                            padding: "2px 8px", borderRadius: 999,
                            textTransform: "uppercase", letterSpacing: ".06em",
                            whiteSpace: "nowrap", flexShrink: 0,
                          }}>★ Your Dominant Mode</span>
                        )}
                      </strong>
                      <em>{def.tagline}</em>
                    </div>
                  </div>
                  <p>{def.overview}</p>
                  <div style={{ fontSize: ".8rem", fontWeight: 700, color: def.color, marginBottom: ".2rem" }}>When to use this mode:</div>
                  <ul className="mode-uses">
                    {def.uses.map((u, i) => <li key={i}>{u}</li>)}
                  </ul>
                  <div className="warn-grid">
                    <div className="warn-high">
                      <div className="warn-label" style={{ color: T.gold }}>⚠ If you scored High</div>
                      <p className="warn-text">{def.highWarning}</p>
                    </div>
                    <div className="warn-low">
                      <div className="warn-label" style={{ color: "#1B6FA0" }}>⚠ If you scored Low</div>
                      <p className="warn-text">{def.lowWarning}</p>
                    </div>
                  </div>
                </div>
                );
              })}

              {/* Context box */}
              <div style={{ background: "#f8f4eb", border: `1px solid ${T.gold}55`, borderRadius: "14px", padding: "1rem", marginBottom: "0" }}>
                <div style={{ fontSize: ".72rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".5rem" }}>
                  Context &amp; Meaning
                </div>
                <p style={{ margin: 0, fontSize: ".83rem", color: T.ink, lineHeight: 1.6 }}>
                  There are no universal right answers. All five modes are useful — each
                  represents a set of valuable social skills. The effectiveness of any mode
                  depends on the specific conflict situation and the skill with which it
                  is deployed.
                </p>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 6 — NORMATIVE COMPARISON & GROWTH
          ══════════════════════════════════════════ */}

          <section className="tk-page">
            <div className="page-inner" style={{ border: "none" }}>
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 6</div>
                  <h2 className="section-title">Assertiveness - Cooperativeness Model</h2>
                </div>
                <div className="small">Mode scores out of 12</div>
              </div>

              <div className="card" style={{ marginTop: "1.25rem" }}>
                <h3>Your Scores on the Assertiveness–Cooperativeness Model</h3>
                <p className="lead" style={{ marginBottom: ".85rem" }}>
                  The Thomas-Kilmann model maps all five conflict-handling modes onto two underlying dimensions:
                  {" "}<strong>assertiveness</strong> (pursuing your own concerns) and{" "}
                  <strong>cooperativeness</strong> (satisfying the other person's concerns).
                  Your combined scores are plotted below — the filled circle marks your position on the grid,
                  and the highlighted mode box shows your active region.
                </p>
                <TKPositionGrid scores={scores} />
              </div>

              <div className="two-col" style = {{gap: "1.25rem", marginTop: "1.25rem"}}>
                <div className="card">
                  <h3>Understanding Your Scores</h3>
                  <p className="lead">
                    The Thomas-Kilmann model shows that each of the five modes reflects a
                    learned pattern of behaviour shaped by predisposition and past success.
                    Rather than being fixed traits, these modes are most effective when used
                    situationally — allowing you to respond flexibly to changing contexts,
                    team dynamics, and the importance of the issue at stake.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    This report is most powerful when reviewed in a guided debrief with a
                    manager, coach, or facilitator. The goal is not to determine where you
                    stand today, but to identify which behaviours to strengthen and which
                    combinations to deploy more deliberately across the situations you face.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════ */}
          <section className="tk-page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
              minHeight: "1556px",
            }}>
              {/* Header bar */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Building Flexible, Adaptive Conflict Management</h2>
                    <p>
                      Conflict-handling behaviour is not fixed. It is a repertoire that expands
                      through self-awareness, deliberate practice, and consistent reflection.
                      Acumen Business Catalyst uses assessments like this to turn behavioural
                      data into focused development conversations that drive lasting change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Practical Conflict Design</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)", textAlign: "justify" }}>
                      For this participant, the most effective conflict management approach should draw on their dominant mode as a natural starting point, while deliberately practicing the modes that are under-utilised. Each of the five modes serves a purpose — the goal is not to change who you are, but to expand your repertoire so you can respond flexibly across different people, pressures, and situations.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact">
                      <span>Brand</span>
                      <strong>Acumen Business Catalyst</strong>
                    </div>
                    <div className="contact">
                      <span>Focus</span>
                      <strong>Leadership · Culture · Capability</strong>
                    </div>
                    <div className="contact">
                      <span>Report type</span>
                      <strong>Individual Conflict Mode Profile</strong>
                    </div>
                  </div>
                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Thomas-Kilmann Conflict Mode Instrument (TKI)
                    assessment framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}